// lib/features/filters/filters.hook.ts
import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../redux/redux.hook";
import {
  setSelected,
  toggleListFilter,
  toggleBooleanFilter,
  toggleSpecialFilter,
  setSort,
  setPriceFilter,
  resetFilters,
  resetServerSelected,
  resetFiltersData,
  clearSelectedOnly,
} from "./filters.slice";
import { getFilters, getGeneralFilters } from "./filters.thunk";
import {
  BooleanFilterKey,
  FilterRequest,
  GeneralFilterColorItem,
  GeneralFilterItem,
  GeneralFiltersRequest,
  GeneralListFilterKey,
  ListFilterKey,
  SelectedFilters,
} from "./filters.types";
import {
  buildFilterRequestFromSelected,
  countGuardedSelections,
  isMinimumGuardedKey,
} from "@/app/utils/filters.utils";
import { isLoading, isSuccess, isError, isIdle } from "@/app/utils/asyncStatus";

const useFilters = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    generalData,
    status,
    generalStatus,
    error,
    selected,
    serverSelected,
    serverSelectedReady,
    serverSelectedSlug,
    userInteractionCount,
    guardedContextSeeded,
  } = useAppSelector((state) => state.filters);

  /** slug: bu filtre isteğinin hangi sayfa için olduğu — serverSelected'ı o slug'a bağlar. */
  const fetchFilters = useCallback(
    async (params: FilterRequest, slug?: string) => {
      const result = await dispatch(
        getFilters({ ...params, ...(slug ? { __slug: slug } : {}) }),
      );
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const fetchGeneralFilters = useCallback(
    async (params: GeneralFiltersRequest) => {
      const result = await dispatch(getGeneralFilters(params));
      return result.meta.requestStatus === "fulfilled";
    },
    [dispatch],
  );

  const updateSelected = useCallback(
    (filters: Partial<SelectedFilters>) => {
      dispatch(setSelected(filters));
    },
    [dispatch],
  );

  const toggleList = useCallback(
    (key: ListFilterKey, value: number | string) => {
      dispatch(toggleListFilter({ key, value: String(value) }));
    },
    [dispatch],
  );

  const toggleGeneralList = useCallback(
    (key: GeneralListFilterKey, value: number | string) => {
      dispatch(toggleListFilter({ key, value: String(value) }));
    },
    [dispatch],
  );

  const toggleBoolean = useCallback(
    (key: BooleanFilterKey) => {
      dispatch(toggleBooleanFilter(key));
    },
    [dispatch],
  );

  const toggleSpecial = useCallback(
    (key: string) => {
      dispatch(toggleSpecialFilter(key));
    },
    [dispatch],
  );

  const updateSort = useCallback(
    (sort: string) => {
      dispatch(setSort(sort));
    },
    [dispatch],
  );

  const setPrice = useCallback(
    (price: { min?: number; max?: number }) => {
      dispatch(setPriceFilter(price));
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  /** Kullanıcı "Tüm Filtreleri Temizle" butonuna bastığında çağrılır.
   *  data/generalData'ya dokunmaz, filtre listesi skeleton göstermez.
   *  initialSelected geçilirse slug bağlamı filtreleri (item_ids, gender vb.) korunur. */
  const clearFilters = useCallback((initialSelected?: Partial<SelectedFilters>) => {
    dispatch(clearSelectedOnly(initialSelected));
  }, [dispatch]);

  const resetServer = useCallback(() => {
    dispatch(resetServerSelected());
  }, [dispatch]);

  const resetData = useCallback(() => {
    dispatch(resetFiltersData());
  }, [dispatch]);

  const headerFilters = useMemo(() => data?.filters.header ?? [], [data]);
  const generalFilters = useMemo(() => data?.filters.general ?? [], [data]);
  const productCount = data?.product_count ?? 0;

  const sortOptions = useMemo(
    () => generalData?.sort_options ?? [],
    [generalData],
  );
  const specialFilters = useMemo(
    () => generalData?.special_filters ?? [],
    [generalData],
  );

  const generalFilterItems = useMemo((): GeneralFilterItem[] => {
    const raw = generalData?.filters ?? [];
    const allowed = new Set(["color", "brand", "price"]);

    return raw
      .filter((f) => allowed.has(f.key))
      .map((filter) => {
        if (filter.type !== "list" || filter.key !== "color") return filter;

        const colorItems = (filter.items as GeneralFilterColorItem[])
          .filter((item) => Boolean(item.hex))
          .map((item) => ({ ...item, hex: item.hex.trim() }));

        return { ...filter, items: colorItems };
      });
  }, [generalData]);

  const isSelected = useCallback(
    (key: ListFilterKey, value: number | string) =>
      (selected[key] as (number | string)[])
        .map(String)
        .includes(String(value)),
    [selected],
  );

  const isGeneralSelected = useCallback(
    (key: GeneralListFilterKey, value: number | string) =>
      (selected[key] as (number | string)[])
        .map(String)
        .includes(String(value)),
    [selected],
  );

  /**
   * Bu tıklama "son kalan bağlam filtresini kaldırma" mı — yani engellenip
   * uyarı gösterilmeli mi?
   *
   * Üç koşul birden gerekiyor:
   *   1. Sayfa bağlamı korunan gruplardan (kategori / ürün grubu / alt kategori)
   *      en az birini seçili getirmiş olmalı. Getirmediyse kural hiç çalışmaz —
   *      kullanıcının kendi seçtiği kategoriyi geri alması engellenmemeli.
   *   2. Tıklanan filtre korunan gruplardan birinde olmalı. Renk/beden/marka
   *      vb. daraltma filtrelerinde kural yok.
   *   3. O üç grupta toplam bir tek seçim kalmış ve tıklanan da o olmalı.
   *
   * Karar burada duruyor ki üç ayrı filtre bileşeni aynı mantığı kopyalamasın.
   */
  const blocksLastContextFilter = useCallback(
    (key: ListFilterKey, value: number | string) => {
      if (!guardedContextSeeded) return false;
      if (!isMinimumGuardedKey(key)) return false;
      if (!isSelected(key, value)) return false;
      return countGuardedSelections(selected) === 1;
    },
    [guardedContextSeeded, isSelected, selected],
  );

  const isSpecialSelected = useCallback(
    (key: string) => {
      if (key === "all") return selected.special_filters.length === 0;
      return selected.special_filters.includes(key);
    },
    [selected],
  );

  const buildFilterRequest = useCallback(
    (base?: Partial<FilterRequest>): FilterRequest =>
      buildFilterRequestFromSelected(selected, base),
    [selected],
  );

  return {
    data,
    generalData,
    status,
    generalStatus,
    error,
    selected,
    headerFilters,
    generalFilters,
    sortOptions,
    specialFilters,
    generalFilterItems,
    productCount,
    isIdle: isIdle(status),
    isLoading: isLoading(status),
    isSuccess: isSuccess(status),
    isError: isError(status),
    isGeneralLoading: isLoading(generalStatus),
    fetchFilters,
    fetchGeneralFilters,
    updateSelected,
    toggleList,
    toggleGeneralList,
    toggleBoolean,
    toggleSpecial,
    updateSort,
    setPrice,
    reset,
    resetServer,
    resetData,
    clearFilters,
    isSelected,
    isGeneralSelected,
    blocksLastContextFilter,
    isSpecialSelected,
    buildFilterRequest,
    serverSelected,
    serverSelectedReady,
    serverSelectedSlug,
    userInteractionCount,
  };
};

export default useFilters;
